import { useEffect, useRef } from 'react'
import { History } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { apiGetPrivateTours } from '../../api/privateTours/privateTourApi'
import { apiGetDayTrips } from '../../api/dayTrips/dayTripApi'
import ProfileModalShell from '../../components/ProfileModalShell'
import SavedTourCard from '../../components/SavedTourCard'
import DayTripCard from '../../components/DayTripCard'
import useSavedPlaces from '../../hooks/useSavedPlaces'

export default function SavedPlaces() {
  const navigate = useNavigate()
  const { savedPlaces, removeSaved, upsertMany } = useSavedPlaces()
  const hydratedRef = useRef(false)

  useEffect(() => {
    if (hydratedRef.current) return

    const stubs = savedPlaces.filter(
      (place) => !place.title || !place.image || !place.description,
    )
    if (stubs.length === 0) {
      hydratedRef.current = true
      return
    }

    let active = true

    Promise.all([apiGetPrivateTours(), apiGetDayTrips()]).then(([privateToursRes, dayTripsRes]) => {
      if (!active) return

      const updates = stubs
        .map((stub) => {
          if (stub.source === 'private-tour' && privateToursRes.success && privateToursRes.data) {
            const tour = privateToursRes.data.find((item) => String(item.id) === stub.id)
            if (tour) {
              return {
                id: String(tour.id),
                source: 'private-tour' as const,
                title: tour.title,
                duration: tour.duration,
                price: String(tour.price),
                image: tour.image,
                explorePath: `/private-tours?id=${tour.id}`,
              }
            }
          } else if (stub.source === 'day-trip' && dayTripsRes.success && dayTripsRes.data) {
             const trip = dayTripsRes.data.find((item) => String(item.id) === stub.id)
             if (trip) {
               return {
                 id: String(trip.id),
                 source: 'day-trip' as const,
                 title: trip.title,
                 duration: trip.duration,
                 price: String(trip.price),
                 image: trip.images?.[0] || (trip as any).image || '',
                 explorePath: `/day-trips?id=${trip.id}`,
                 subtitle: trip.subtitle || 'Salzburg Day Tour',
                 description: trip.description || 'Scenic alpine journey with private driver and breathtaking landscapes.',
                 tags: trip.features || (trip as any).tags || ['Hotel pickup', 'Private vehicle', 'Scenic route'],
               }
             }
          }
          return null
        })
        .filter((item): item is NonNullable<typeof item> => Boolean(item))

      if (updates.length > 0) upsertMany(updates)
      hydratedRef.current = true
    })

    return () => {
      active = false
    }
  }, [savedPlaces, upsertMany])

  const privateTours = savedPlaces.filter(
    (place) => place.source === 'private-tour' && place.title && place.image,
  )

  const dayTrips = savedPlaces.filter(
    (place) => place.source === 'day-trip' && place.title && place.image,
  )

  return (
    <ProfileModalShell>
      <h1 className="font-host text-[24px] font-semibold leading-[1.69] tracking-[-0.72px] text-[#1C1C1C]">
        Saved places
      </h1>

      {privateTours.length > 0 || dayTrips.length > 0 ? (
        <div className="mt-8 flex flex-col gap-10">
          
          {privateTours.length > 0 && (
            <div>
              <h2 className="font-host text-[20px] font-semibold text-[#1C1C1C] mb-4">
                Private Van Tours
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:flex md:flex-wrap gap-x-[16px] gap-y-[24px]">
                {privateTours.map((place) => (
                  <SavedTourCard
                    key={`${place.source}-${place.id}`}
                    place={place}
                    onExplore={() => navigate(place.explorePath)}
                    onRemove={() => removeSaved(place.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {dayTrips.length > 0 && (
            <div>
              <h2 className="font-host text-[20px] font-semibold text-[#1C1C1C] mb-4">
                Day Trips
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:flex md:flex-wrap gap-x-[20px] gap-y-[24px]">
                {dayTrips.map((place) => (
                  <DayTripCard
                    key={`${place.source}-${place.id}`}
                    id={place.id}
                    title={place.title}
                    subtitle={place.subtitle || 'Day Tour'}
                    description={place.description || 'Experience stunning alpine scenery and unforgettable landmarks with Salzburg Cab.'}
                    image={place.image}
                    tags={place.tags || ['Hotel pickup', 'Private chauffeur', 'Scenic route']}
                    price={place.price}
                    timeBadge={place.duration || '8–10 Hours'}
                    morePath={place.explorePath}
                    isSaved={true}
                    onSaveToggle={() => removeSaved(place.id)}
                    onExplore={() => navigate(place.explorePath)}
                  />
                ))}
              </div>
            </div>
          )}

        </div>
      ) : (
        <div className="mt-8 grid h-[165px] w-[175px] place-items-center rounded-[13px] bg-[#414141] text-center text-white shadow-sm">
          <div className="flex flex-col items-center">
            <History className="h-9 w-9 stroke-[1.6]" />
            <span className="mt-3 font-host text-[12px] font-normal text-white/95">
              No saved places yet
            </span>
          </div>
        </div>
      )}
    </ProfileModalShell>
  )
}
